import React from 'react'
import { InferType } from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { societyRequestSchema } from '../../../../forms/schemas/society_request'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { UnitRole } from '../../../../types/unit-roles'
import { getUnitRole } from '../../../../helpers/translations/unit-roles'
import { ToastType } from '../../../common/Toast/toast-type'
import { NRequests } from '../../../../interfaces/services/requests.interfaces'
import { SocietyAvatar } from '../../../common/Avatar'
import { theme } from '../../../../theme/theme'
import { FormControl } from '../../../common/FormControl'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { Button, ButtonVariant } from '../../../common/Button'
import { SocietyInformation } from '../SocietyInformation'
import { ISocietyWithFiles } from '../../../../interfaces/services/societies.interfaces'
import { entranceQueries } from '../../../../api/society-entrances'
import { unitQueries } from '../../../../api/units'
import { createRequest } from '../../../../api/requests'
import { useAuth } from '../../../../hooks/useAuth'
import { i18nSocietyType } from '../../../../i18n'

interface RequestJoinSocietyFormProps {
  society: ISocietyWithFiles
  onFinish?: () => void
}

export const RequestJoinSocietyForm = ({
  society,
  onFinish,
}: RequestJoinSocietyFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId } = useAuth()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()

  const getDefaultValues = (): InferType<typeof societyRequestSchema> => {
    return {
      unitId: '',
      entranceId: '',
      role: UnitRole.MEMBER,
    }
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyRequestSchema),
    defaultValues: getDefaultValues(),
  })

  const watchEntranceId = watch('entranceId')

  const { data: entrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society._id)
  )

  const entranceOptions =
    entrances?.map((entrance) => ({
      label: entrance.addressStreet || '',
      value: entrance._id,
    })) || []

  const { data: units } = useQuery(unitQueries.getUnitsPerSociety(society._id))

  const entranceUnitOptions =
    units
      ?.filter((unit) => {
        return unit.entranceId === watchEntranceId
      })
      .map((unit) => {
        if (unit.isUnassigned) {
          return {
            value: unit._id,
            label: translate('common.unit.unassigned'),
          }
        }
        return {
          value: unit._id,
          label: `${unit.titleLegal} (${unit.title})`,
        }
      }) || []

  const unitRoleOptions = Object.values(UnitRole).map((val) => ({
    value: val as string,
    label: translate(getUnitRole(val) as string),
  }))

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('requestJoinSocietyForm.flashMessage.sendRequestFailure')
    )
  }

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('requestJoinSocietyForm.flashMessage.sendRequestSuccess')
    )
  }

  const { mutate: mutateCreateRequest, isPending: isSendingRequest } =
    useMutation({
      mutationFn: createRequest,
      onSuccess: () => {
        showSuccessMessage()
        if (onFinish) onFinish()
      },
      onError: () => {
        showErrorMessage()
      },
    })
  const onSubmit = async (
    data: InferType<typeof societyRequestSchema>
  ): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const body: NRequests.NCreate.IRequestBody = {
      ...data,
      userId: userId as string,
      societyId: society._id as string,
    }

    userId && mutateCreateRequest(body)
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isSendingRequest

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit, onError)}>
      <p style={theme.textVariants.h3}>
        {translate('requestJoinSocietyForm.title', {
          context: i18nSocietyType(society.societyTypeDisplayName),
        })}
      </p>
      <div className="flex flex-col items-center gap-1">
        <SocietyAvatar society={society} size={96} />
        <SocietyInformation society={society} />
      </div>
      <div className="border-b" />
      <Controller
        control={control}
        name="entranceId"
        render={({ field: { value, name, onChange } }) => (
          <FormControl
            label={translate('common.form.labels.entrance', {
              context: i18nSocietyType(society.societyTypeDisplayName),
            })}
            error={errors.entranceId && getErrorMessage(errors.entranceId)}
            name={name}
          >
            <SelectDropdown
              options={entranceOptions}
              value={entranceOptions.find((option) => option.value === value)}
              onChange={(option) => {
                onChange(option?.value)
                setValue('unitId', '', { shouldValidate: true })
              }}
              error={!!errors.entranceId}
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="unitId"
        render={({ field: { value, name, onChange } }) => (
          <FormControl
            label={translate('common.form.labels.unit', {
              context: i18nSocietyType(society.societyTypeDisplayName),
            })}
            error={errors.unitId && getErrorMessage(errors.unitId)}
            name={name}
            infoTooltipTitle={translate('requestJoinSocietyForm.tooltip.unit', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
          >
            <SelectDropdown
              options={entranceUnitOptions}
              value={entranceUnitOptions.find(
                (option) => option.value === value
              )}
              onChange={(option) => onChange(option?.value)}
              error={!!errors.unitId}
              menuPlacement="auto"
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="role"
        render={({ field: { value, name, onChange } }) => (
          <FormControl
            label={translate('common.form.labels.unitRole')}
            error={errors.role && getErrorMessage(errors.role)}
            name={name}
            infoTooltipTitle={translate('requestJoinSocietyForm.tooltip.role', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
          >
            <SelectDropdown
              options={unitRoleOptions}
              value={unitRoleOptions.find((option) => option.value === value)}
              onChange={(option) => onChange(option?.value)}
              error={!!errors.role}
            />
          </FormControl>
        )}
      />
      <Button
        wrapperClassName="w-full"
        className="w-full"
        variant={ButtonVariant.PRIMARY}
        loading={loading}
        disabled={!isValid || loading}
        label={translate('requestJoinSocietyForm.sendRequest')}
        type="submit"
      />
    </form>
  )
}
